import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../../components/ui";
import { useVehicles } from "../hooks/useVehicles";
import { useMxLabels } from "../hooks/useMxLabels";
import { useMaintenanceActions } from "../hooks/useMaintenanceActions";
import { useMaintenanceCapabilities } from "../hooks/useMaintenanceCapabilities";
import { fetchMaintenanceDue } from "../services/maintenanceService";
import { STATUS_ACTIONS, STATUS_TRANSITIONS } from "../theme";
import { daysUntil, dueOf, startOfDay } from "../lib";
import type { MaintenanceDueBuckets, MaintenanceRecord, MaintenanceStatus } from "../types";
import { DueText, IconAction, KpiCard, LoadingBlock, PageShell, SelectFilter, StatusBadge, ToolbarButton, TypeBadge } from "../components/kit/Primitives";
import { VehicleThumb } from "../components/kit/Media";
import { MaintenanceDrawer } from "../components/kit/MaintenanceDrawer";
import { MANTENIMIENTO_BASE } from "../paths";

const EMPTY: MaintenanceDueBuckets = {
  overdue: [],
  upcoming: [],
  pending: [],
  counts: { overdue: 0, upcoming: 0, pending: 0, open: 0 },
  withinDays: 7,
};

interface Group {
  key: string;
  title: string;
  subtitle?: string;
  tone: "error" | "warning" | "info" | "neutral";
  rows: MaintenanceRecord[];
}

// Seguimiento y recordatorios: línea de tiempo de lo abierto. Arriba los
// vencidos, luego un bloque por día (hoy, mañana…) dentro de la ventana y al
// final lo pendiente sin fecha. Cada fila avanza de estado en línea.
export default function SeguimientoSection() {
  const L = useMxLabels();
  const { t } = L;
  const navigate = useNavigate();
  const { capabilities } = useMaintenanceCapabilities();
  const { vehicleOf } = useVehicles();
  const [data, setData] = useState<MaintenanceDueBuckets>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [withinDays, setWithinDays] = useState(15);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchMaintenanceDue(withinDays));
    } catch {
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [withinDays]);

  useEffect(() => {
    load();
  }, [load]);

  const actions = useMaintenanceActions(load);

  const groups: Group[] = useMemo(() => {
    const out: Group[] = [];
    const byDue = (a: MaintenanceRecord, b: MaintenanceRecord) => Date.parse(dueOf(a) || "") - Date.parse(dueOf(b) || "");
    // Lo que ya está en taller no es «vencido» aunque su fecha haya pasado.
    const late = data.overdue.filter((r) => r.status !== "en_proceso");
    const inShop = [...data.overdue, ...data.upcoming].filter((r) => r.status === "en_proceso");
    if (late.length) out.push({ key: "overdue", title: t("mantenimiento.ui.timeline.overdue"), tone: "error", rows: late.sort(byDue) });
    if (inShop.length) out.push({ key: "inshop", title: L.status("en_proceso"), tone: "info", rows: inShop.sort(byDue) });
    const days = new Map<number, MaintenanceRecord[]>();
    for (const r of data.upcoming.filter((x) => x.status !== "en_proceso")) {
      const d = daysUntil(dueOf(r)) ?? 0;
      days.set(d, [...(days.get(d) || []), r]);
    }
    [...days.keys()].sort((a, b) => a - b).forEach((d) => {
      const date = new Date(startOfDay(Date.now()) + d * 86400000);
      const label = date.toLocaleDateString(L.locale, { weekday: "long", day: "numeric", month: "long" });
      out.push({
        key: `d${d}`,
        title: d === 0 ? t("mantenimiento.ui.timeline.today") : d === 1 ? t("mantenimiento.ui.timeline.tomorrow") : label,
        subtitle: d <= 1 ? label : undefined,
        tone: d <= 2 ? "warning" : "info",
        rows: (days.get(d) || []).sort(byDue),
      });
    });
    const noDate = data.pending.filter((r) => !dueOf(r));
    const later = data.pending.filter((r) => dueOf(r));
    if (later.length) out.push({ key: "later", title: t("mantenimiento.ui.timeline.later"), tone: "neutral", rows: later.sort(byDue) });
    if (noDate.length) out.push({ key: "nodate", title: t("mantenimiento.ui.timeline.pendingNoDate"), tone: "neutral", rows: noDate });
    return out;
  }, [data, t, L]);

  const all = [...data.overdue, ...data.upcoming, ...data.pending];
  const selected = all.find((r) => r.id === selectedId) || null;
  const todayCount = data.upcoming.filter((r) => daysUntil(dueOf(r)) === 0).length;

  const dot = { error: "bg-error", warning: "bg-warning", info: "bg-info", neutral: "bg-content-muted" } as const;

  return (
    <PageShell
      icon="ri-radar-line"
      title={t("mantenimiento.menu.seguimiento")}
      subtitle={t("mantenimiento.seguimientoSubtitle")}
      actions={
        <>
          <SelectFilter value={String(withinDays)} onChange={(v) => setWithinDays(Number(v))} label={t("mantenimiento.due.window")} options={[7, 15, 30, 60].map((d) => ({ value: String(d), label: t("mantenimiento.due.nextDays", { days: d }) }))} />
          <ToolbarButton icon="ri-refresh-line" label={t("mantenimiento.ui.refresh")} onClick={load} spinning={loading} />
        </>
      }
    >
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard icon="ri-alarm-warning-line" tone="error" label={t("mantenimiento.due.overdue")} value={data.overdue.filter((r) => r.status !== "en_proceso").length} loading={loading} />
        <KpiCard icon="ri-sun-line" tone="warning" label={t("mantenimiento.ui.timeline.today")} value={todayCount} loading={loading} />
        <KpiCard icon="ri-time-line" tone="info" label={t("mantenimiento.due.upcoming", { days: data.withinDays })} value={data.counts.upcoming} loading={loading} />
        <KpiCard icon="ri-inbox-line" tone="neutral" label={t("mantenimiento.due.pending")} value={data.counts.pending} loading={loading} />
      </div>

      <p className="mb-3 text-xs text-content-muted">
        <i className="ri-git-commit-line mr-1" />
        {t("mantenimiento.ui.timeline.legend", { days: withinDays })}
      </p>

      {loading && !all.length ? (
        <LoadingBlock />
      ) : groups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-card px-6 py-12 text-center text-sm text-content-muted">
          <i className="ri-checkbox-circle-line mb-2 block text-3xl text-success" />
          {t("mantenimiento.ui.allClear")}
        </div>
      ) : (
        <ol className="relative ml-3 border-l-2 border-line">
          {groups.map((g) => (
            <li key={g.key} className="mb-6 pl-6">
              <span className={cn("absolute -left-[9px] mt-1 h-4 w-4 rounded-full ring-4 ring-app", dot[g.tone])} />
              <div className="mb-2 flex items-baseline gap-2">
                <h3 className={cn("text-sm font-semibold first-letter:uppercase", g.tone === "error" ? "text-error" : "text-content")}>{g.title}</h3>
                {g.subtitle ? <span className="inline-block text-xs text-content-muted first-letter:uppercase">{g.subtitle}</span> : null}
                <span className="rounded-full bg-subtle px-2 text-xs font-semibold text-content-secondary">{g.rows.length}</span>
              </div>
              <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                {g.rows.map((r) => {
                  const v = vehicleOf(r.vehicleId);
                  const status = r.status || "pendiente";
                  const nexts = capabilities.canManageRecords ? STATUS_TRANSITIONS[status] ?? [] : [];
                  return (
                    <div
                      key={r.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedId(r.id)}
                      onKeyDown={(e) => e.key === "Enter" && setSelectedId(r.id)}
                      className={cn("flex cursor-pointer items-center gap-3 rounded-xl border bg-card p-2.5 transition-colors hover:bg-subtle/40", g.tone === "error" ? "border-error/40" : "border-line")}
                    >
                      <VehicleThumb vehicle={v} size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate text-sm font-medium text-content">{v?.label ?? r.vehicleId}</p>
                        </div>
                        <p className="truncate text-xs text-content-secondary">{r.maintenanceDescription}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <StatusBadge record={r} size="sm" />
                          <TypeBadge type={r.type} />
                          <DueText record={r} />
                        </div>
                      </div>
                      {nexts.length ? (
                        <div className="flex shrink-0 flex-col gap-0.5 sm:flex-row">
                          {nexts.map((s) => (
                            <IconAction key={s} icon={STATUS_ACTIONS[s]?.icon ?? "ri-arrow-right-line"} label={L.transition(s)} className={cn("rounded-md p-1.5", STATUS_ACTIONS[s]?.className)} onClick={() => actions.transition(r, s as MaintenanceStatus)} />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ol>
      )}

      <p className="mt-2 text-xs text-content-muted">
        <i className="ri-information-line mr-1" />
        {t("mantenimiento.seguimientoHint")}
      </p>

      {selected ? (
        <MaintenanceDrawer
          record={selected}
          vehicle={vehicleOf(selected.vehicleId)}
          onClose={() => setSelectedId(null)}
          canManage={capabilities.canManageRecords}
          canCharge={capabilities.canCharge}
          onTransition={actions.transition}
          onPdf={actions.workOrderPdf}
          onCharge={actions.charge}
          onOpenVehicle={(id) => navigate(`${MANTENIMIENTO_BASE}/historial?vehicle=${encodeURIComponent(id)}`)}
        />
      ) : null}
    </PageShell>
  );
}
